/**
 * App.Interaction - 拾取 / 编辑操作 / 事件
 *
 * 负责所有用户交互：鼠标拾取、模式切换、对象编辑等
 */

import * as THREE from 'three';
import type { Room, End, Vertex, Cell, Shaft } from '../types';
import { TileType } from '../types';
import { App } from './App';

/* ─────────────── Module: Interaction（拾取 / 编辑操作 / 事件）─────────────── */
App.Interaction = {
  /** 射线投射器 */
  ray: new THREE.Raycaster(),

  /** 鼠标位置 */
  mouse: new THREE.Vector2(),

  /** 地面平面（用于拾取格子） */
  groundPlane: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),

  /**
   * 从事件获取格子坐标
   */
  cellFromEvent(ev: Event): Cell | null {
    this.mouse.x = ((ev as MouseEvent).clientX / innerWidth) * 2 - 1;
    this.mouse.y = -((ev as MouseEvent).clientY / innerHeight) * 2 + 1;

    this.ray.setFromCamera(this.mouse, App.Render.activeCamera());

    const hit = new THREE.Vector3();
    this.groundPlane.constant = -(App.Store.f?.yBase || 0); // 拾取平面落在活动楼层

    if (!this.ray.ray.intersectPlane(this.groundPlane, hit)) return null;

    const G = App.Config.GRID;
    const gx = Math.floor(hit.x + G.W / 2);
    const gz = Math.floor(hit.z + G.D / 2);

    return (App.World as any).inB(gx, gz) ? { x: gx, z: gz } : null;
  },

  /**
   * 射线检测末端对象
   */
  raycastEnd(ev: Event): THREE.Mesh | null {
    this.mouse.x = ((ev as MouseEvent).clientX / innerWidth) * 2 - 1;
    this.mouse.y = -((ev as MouseEvent).clientY / innerHeight) * 2 + 1;

    this.ray.setFromCamera(this.mouse, App.Render.activeCamera());

    const cands = App.Render.gEnd.children.filter((c: any) => c.userData && c.userData.end);
    const hits = this.ray.intersectObjects(cands, false);

    return hits.length ? hits[0].object as THREE.Mesh : null;
  },

  // ════════════════════════════════════════════════════════════════════
  // 连线操作
  // ════════════════════════════════════════════════════════════════════

  /**
   * 完成当前线条
   */
  finishLine(): void {
    const S = App.Store;
    const W = App.World;
    const RR = App.Render;
    const UI = App.UI;

    if (S.mode !== 'line') return;
    if (S.curLine.length < 2) {
      UI.setProgress('线条至少需要 2 个顶点，继续点击或按 Esc 取消');
      return;
    }

    if ((App.Command as any).do('完成走管线', () => this._finishLine())) {
      UI.setProgress(
        `✓ 已定义第 ${S.f.lines.length} 条走管线 · 交接点 ${S.f.junctions.length} · 点击"生成连接路径"`
      );
    }
  },

  _finishLine(): void {
    const S = App.Store;
    const W = App.World;
    const cells = W.rasterizeVertices(S.curLine);

    S.f.lines.push({ vertices: S.curLine.slice(), cells });
    W.applyLineToGrid(cells);
    W.recomputeJunctions();
    S.curLine = [];
  },

  /**
   * 取消当前线条
   */
  cancelLine(): void {
    const S = App.Store;
    const RR = App.Render;
    const UI = App.UI;

    if (!S.curLine.length) return;
    S.curLine = [];
    RR.buildPreview();
    UI.updateDrawStatus();
    if (S.mode === 'line') UI.setProgress('已取消当前线条');
  },

  /**
   * 清空所有线条
   */
  clearLines(): void {
    const S = App.Store;
    const UI = App.UI;

    if (!S.f.lines.length) {
      UI.setProgress('没有走管线条可清空');
      return;
    }

    if ((App.Command as any).do('清空走管线', () => this._clearLines())) {
      UI.setProgress('已清空所有走管线条');
    }
  },

  _clearLines(): void {
    const S = App.Store;
    const W = App.World;
    const f = S.f;
    const T = App.Config.T;

    for (const [k, orig] of f.drawnOrig) {
      const [x, z] = k.split(',').map(Number);
      if (W.inB(x, z)) f.type[x][z] = orig;
    }
    f.drawnOrig.clear();
    f.drawnCells.clear();
    f.lines = [];
    f.junctions = [];
    S.curLine = [];
  },

  // ════════════════════════════════════════════════════════════════════
  // 末端操作
  // ════════════════════════════════════════════════════════════════════

  /**
   * 添加末端
   */
  addEnd(cell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const RR = App.Render;
    const UI = App.UI;
    const C = App.Config;
    const f = S.f;
    const T = C.T;

    if (!W.inB(cell.x, cell.z)) return;
    const t = f.type[cell.x][cell.z];

    if (t === T.ROOM) {
      UI.setProgress('该格为设备机房，无法放置末端');
      return;
    }
    if (t === T.END) {
      UI.setProgress('该格已有末端');
      return;
    }

    if ((App.Command as any).do('添加末端', () => this._addEnd(cell))) {
      UI.setProgress(`已添加 末端${S.endCounter} · 共 ${f.ends.length} 个末端`);
    }
  },

  _addEnd(cell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const C = App.Config;
    const f = S.f;
    const T = C.T;
    const t = f.type[cell.x][cell.z];

    S.endCounter++;
    const e: End = {
      id: '末端' + S.endCounter,
      x: cell.x,
      z: cell.z,
      color: C.END_COLORS[(S.endCounter - 1) % C.END_COLORS.length],
      added: true,
      floorIdx: S.activeFloorIdx,
      systemId: S.activeSystemId,
    };

    const k = cell.x + ',' + cell.z;
    if (!f.endOrig.has(k)) f.endOrig.set(k, t);
    f.type[cell.x][cell.z] = T.END;
    f.ends.push(e);
  },

  /**
   * 实时移动末端（拖拽中）
   */
  _liveMoveEnd(end: End, newCell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const RR = App.Render;
    const C = App.Config;
    const f = S.f;
    const T = C.T;

    if (!W.inB(newCell.x, newCell.z)) return;
    if (newCell.x === end.x && newCell.z === end.z) return;

    const nt = f.type[newCell.x][newCell.z];
    if (nt === T.ROOM || nt === T.END) return;

    const ok = end.x + ',' + end.z;
    f.type[end.x][end.z] = f.endOrig.has(ok) ? f.endOrig.get(ok)! : T.FORBID;
    f.endOrig.delete(ok);

    const nk = newCell.x + ',' + newCell.z;
    if (!f.endOrig.has(nk)) f.endOrig.set(nk, nt);
    f.type[newCell.x][newCell.z] = T.END;

    end.x = newCell.x;
    end.z = newCell.z;
    RR.buildTiles();
  },

  /**
   * 删除末端
   */
  deleteEnd(end: End): void {
    const UI = App.UI;

    if ((App.Command as any).do('删除末端', () => this._deleteEnd(end))) {
      UI.setProgress(`已删除 ${end.id} · 剩余 ${App.Store.f.ends.length} 个末端`);
    }
  },

  _deleteEnd(end: End): void {
    const S = App.Store;
    const W = App.World;
    const C = App.Config;
    const f = S.f;
    const T = C.T;

    const i = f.ends.indexOf(end);
    if (i < 0) return;

    const ok = end.x + ',' + end.z;
    f.type[end.x][end.z] = f.endOrig.has(ok) ? f.endOrig.get(ok)! : T.FORBID;
    f.endOrig.delete(ok);
    f.ends.splice(i, 1);

    for (const fl of S.floors) {
      for (const s of fl.bindings.values()) {
        s.delete(end.id);
      }
    }
  },

  /**
   * 清除自建末端
   */
  clearAddedEnds(): void {
    const S = App.Store;
    const UI = App.UI;

    if (S.running) return;
    if (!S.f.ends.some(e => e.added)) {
      UI.setProgress('没有自建末端可清除');
      return;
    }

    if ((App.Command as any).do('清除自建末端', () => this._clearAddedEnds())) {
      UI.setProgress(`已清除自建末端 · 剩余 ${S.f.ends.length} 个`);
    }
  },

  _clearAddedEnds(): void {
    const S = App.Store;
    const W = App.World;
    const C = App.Config;
    const f = S.f;
    const T = C.T;

    const rem = f.ends.filter(e => e.added);
    for (const e of rem) {
      const ok = e.x + ',' + e.z;
      f.type[e.x][e.z] = f.endOrig.has(ok) ? f.endOrig.get(ok)! : T.FORBID;
      f.endOrig.delete(ok);

      for (const fl of S.floors) {
        for (const s of fl.bindings.values()) {
          s.delete(e.id);
        }
      }

      const i = f.ends.indexOf(e);
      if (i >= 0) f.ends.splice(i, 1);
    }
  },

  // ════════════════════════════════════════════════════════════════════
  // 机房操作
  // ════════════════════════════════════════════════════════════════════

  /**
   * 添加机房
   */
  addRoom(cell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const RR = App.Render;
    const UI = App.UI;
    const C = App.Config;

    const r: Room = {
      id: '机房' + (S.roomCounter + 1),
      x: cell.x,
      z: cell.z,
      color: C.ROOM_COLOR,
      shape: C.ROOM_SHAPE_SMALL,
      floorIdx: S.activeFloorIdx,
      systemId: S.activeSystemId,
      added: true,
    };

    const a = W.clampAnchor(r, cell.x, cell.z);
    if (!W.canPlaceAt(r, a.x, a.z)) {
      UI.setProgress('该处无法放置机房（压到末端/机房或越界）');
      return;
    }

    if ((App.Command as any).do('添加机房', () => this._addRoom(cell))) {
      UI.setProgress(`已添加 机房${S.roomCounter} · 共 ${S.f.rooms.length} 个机房`);
    }
  },

  _addRoom(cell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const C = App.Config;

    const r: Room = {
      id: '机房' + (S.roomCounter + 1),
      x: cell.x,
      z: cell.z,
      color: C.ROOM_COLOR,
      shape: C.ROOM_SHAPE_SMALL,
      added: true,
      floorIdx: S.activeFloorIdx,
      systemId: S.activeSystemId,
    };

    const a = W.clampAnchor(r, cell.x, cell.z);
    S.roomCounter++;
    r.x = a.x;
    r.z = a.z;
    W.commitRoomCells(r);
    S.f.rooms.push(r);
  },

  /**
   * 实时移动机房（拖拽中）
   */
  _liveMoveRoom(r: Room, anchorCell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const RR = App.Render;

    const a = W.clampAnchor(r, anchorCell.x - S.roomGrabDX, anchorCell.z - S.roomGrabDZ);
    if (a.x === r.x && a.z === r.z) return;
    if (!W.canPlaceAt(r, a.x, a.z)) return;

    W.eraseRoomCells(r);
    r.x = a.x;
    r.z = a.z;
    W.commitRoomCells(r);
    RR.buildTiles();
  },

  /**
   * 删除机房
   */
  deleteRoom(r: Room): void {
    const UI = App.UI;

    if ((App.Command as any).do('删除机房', () => this._deleteRoom(r))) {
      UI.setProgress(`已删除 ${r.id} · 剩余 ${App.Store.f.rooms.length} 个机房`);
    }
  },

  _deleteRoom(r: Room): void {
    const S = App.Store;
    const W = App.World;
    const f = S.f;

    const i = f.rooms.indexOf(r);
    if (i < 0) return;

    W.eraseRoomCells(r);
    f.rooms.splice(i, 1);
    f.bindings.delete(r.id);
  },

  /**
   * 清除自建机房
   */
  clearAddedRooms(): void {
    const S = App.Store;
    const UI = App.UI;

    if (S.running) return;
    if (!S.f.rooms.some(r => r.added)) {
      UI.setProgress('没有自建机房可清除');
      return;
    }

    if ((App.Command as any).do('清除自建机房', () => this._clearAddedRooms())) {
      UI.setProgress(`已清除自建机房 · 剩余 ${S.f.rooms.length} 个`);
    }
  },

  _clearAddedRooms(): void {
    const S = App.Store;
    const W = App.World;
    const f = S.f;

    const rem = f.rooms.filter(r => r.added);
    for (const r of rem) {
      W.eraseRoomCells(r);
      f.bindings.delete(r.id);
      const i = f.rooms.indexOf(r);
      if (i >= 0) f.rooms.splice(i, 1);
    }
  },

  // ════════════════════════════════════════════════════════════════════
  // 绑定操作
  // ════════════════════════════════════════════════════════════════════

  /**
   * 设置绑定关系
   */
  setBind(rid: string, eid: string, checked: boolean): void {
    const S = App.Store;

    // 如果不在绑定模式下，自动开启绑定模式
    if (!S.bindMode && checked) {
      (App.Command as any).do('开启手动绑定', () => {
        S.bindMode = true;
      });
    }

    (App.Command as any).do(checked ? '绑定 ' + eid : '解绑 ' + eid, () => {
      let owner: any = null;
      for (const f of S.floors) {
        if (f.rooms.some((r: Room) => r.id === rid)) {
          owner = f;
          break;
        }
      }
      if (!owner) return;

      if (!owner.bindings.has(rid)) owner.bindings.set(rid, new Set());
      const s = owner.bindings.get(rid)!;
      if (checked) {
        s.add(eid);
      } else {
        s.delete(eid);
      }
    });
  },

  /**
   * 切换绑定模式
   */
  setBindMode(on: boolean): void {
    const S = App.Store;
    const RR = App.Render;
    const UI = App.UI;

    (App.Command as any).do(on ? '开启手动绑定' : '关闭手动绑定', () => {
      S.bindMode = on;
    });

    // 输出绑定状态
    (App.Routing as any).dumpBindings();

    UI.setProgress(
      S.bindMode
        ? '已开启手动绑定 · 勾选每个机房要连接的末端'
        : '已关闭手动绑定 · 自动取最近机房'
    );
  },

  /**
   * 修改对象颜色
   */
  setObjColor(obj: Room | End, hex: string): void {
    const S = App.Store;
    const f = S.f;

    const newColor = parseInt(hex.replace('#', ''), 16);

    const target =
      f.rooms.find((r: Room) => r.id === obj.id) || f.ends.find((e: End) => e.id === obj.id);

    if (!target) return;

    (App.Command as any).do('修改颜色', () => {
      (target as any).color = newColor;
    });

    (App.Selection as any).afterChange();
  },

  /**
   * 修改对象所属管线系统
   */
  setObjSystem(obj: Room | End, systemId: number): void {
    const S = App.Store;
    const f = S.f;

    const target =
      f.rooms.find((r: Room) => r.id === obj.id) || f.ends.find((e: End) => e.id === obj.id);

    if (!target) return;

    (App.Command as any).do('修改管线系统', () => {
      (target as any).systemId = systemId;
    });

    (App.Selection as any).afterChange();
  },

  /**
   * 批量修改多个末端的管线系统（一次撤销）
   */
  setEndsSystem(endIds: string[], systemId: number): void {
    const S = App.Store;
    const idSet = new Set(endIds);

    (App.Command as any).do('批量修改末端系统', () => {
      for (const f of S.floors) {
        for (const e of f.ends) {
          if (idSet.has(e.id)) e.systemId = systemId;
        }
      }
    });

    const sysName = (App.Routing as any).systemNameOf(systemId);
    App.UI.setProgress(`已将 ${endIds.length} 个末端设为「${sysName}」系统`);
  },

  /**
   * 批量重命名：按 系统/楼层 筛选机房或末端，用前缀+起始号顺号命名，同步更新绑定
   */
  setBatchRename(kind: string, sys: string, floor: string, prefix: string, startNum: number): void {
    const S = App.Store;
    const sysFilter = sys === 'all' ? null : +sys;
    const floorFilter = floor === 'all' ? null : +floor;

    // 预览数量（do 内外筛选条件一致）
    let count = 0;
    S.floors.forEach((f: any, fi: number) => {
      if (floorFilter !== null && fi !== floorFilter) return;
      const list = kind === 'room' ? f.rooms : f.ends;
      list.forEach((o: any) => {
        if (sysFilter === null || o.systemId === sysFilter) count++;
      });
    });

    if (!count) {
      App.UI.setProgress('没有符合条件的对象可重命名');
      return;
    }

    (App.Command as any).do('批量重命名', () => {
      const targets: any[] = [];
      S.floors.forEach((f: any, fi: number) => {
        if (floorFilter !== null && fi !== floorFilter) return;
        const list = kind === 'room' ? f.rooms : f.ends;
        list.forEach((o: any) => {
          if (sysFilter === null || o.systemId === sysFilter) targets.push(o);
        });
      });

      const idMap = new Map<string, string>();
      targets.forEach((o: any, i: number) => {
        const oldId = o.id;
        const newId = prefix + (startNum + i);
        o.id = newId;
        if (oldId !== newId) idMap.set(oldId, newId);
      });

      // 同步绑定：机房 id 是 bindings 的 key；末端 id 在 value 的 Set 中
      S.floors.forEach((f: any) => {
        if (kind === 'room') {
          const nb = new Map<string, Set<string>>();
          for (const [rid, set] of f.bindings) {
            nb.set(idMap.get(rid) ?? rid, set);
          }
          f.bindings = nb;
        } else {
          for (const set of f.bindings.values()) {
            const changes: string[] = [];
            for (const eid of set) if (idMap.has(eid)) changes.push(eid);
            for (const eid of changes) {
              set.delete(eid);
              set.add(idMap.get(eid)!);
            }
          }
        }
      });
    });

    const what = kind === 'room' ? '机房' : '末端';
    App.UI.setProgress(`已批量重命名 ${count} 个${what}（${prefix}${startNum} 起）`);
  },

  /**
   * 修改管线系统名称/颜色/简称
   */
  setSystemProp(sysId: number, name: string, color: number, abbr?: string): void {
    const S = App.Store;
    const sys = S.systems.find(s => s.id === sysId);
    if (!sys) return;

    (App.Command as any).do('修改系统属性', () => {
      if (name) sys.name = name;
      sys.color = color;
      if (abbr !== undefined) sys.abbr = abbr;
    });

    App.UI.legend();
    App.UI.renderSystemPanel();
  },

  /**
   * 新增管线系统
   */
  addSystem(): void {
    const S = App.Store;
    const C = App.Config;
    (App.Command as any).do('新增管线系统', () => {
      S.systemCounter++;
      S.systems.push({
        id: S.systemCounter,
        name: '系统' + S.systemCounter,
        color: C.END_COLORS[(S.systemCounter - 1) % C.END_COLORS.length],
        abbr: '',
      });
    });
    App.UI.legend();
    App.UI.renderSystemPanel();
  },

  /**
   * 设为当前活动系统（新建机房/末端默认归属）
   */
  setActiveSystem(sysId: number): void {
    App.Store.activeSystemId = sysId;
    App.UI.renderSystemPanel();
  },

  // ════════════════════════════════════════════════════════════════════
  // 重新生成场景
  // ════════════════════════════════════════════════════════════════════

  /**
   * 重新生成场景
   */
  regen(): void {
    const S = App.Store;
    const RR = App.Render;
    const W = App.World;
    const UI = App.UI;

    if (S.running) return;

    S.hoverCell = null;
    S.dragEnd = null;
    S.dragRoom = null;
    S.downCell = null;
    if (RR.activeControls) (RR.activeControls() as any).enabled = true;

    if ((App.Command as any).do('重新生成场景', () => this._regen())) {
      UI.setProgress('已重新生成场景 · 点击"生成连接路径"');
    }
  },

  _regen(): void {
    const S = App.Store;
    const W = App.World;
    const f = S.f;

    const hadRoom = f.rooms.length > 0;
    const isF0 = S.activeFloorIdx === 0;

    f.lines = [];
    f.drawnCells.clear();
    f.drawnOrig.clear();
    f.junctions = [];
    f.bindings.clear();
    f.riserOrig.clear();
    S.curLine = [];
    S.bindMode = false;

    W.genLayout(f, S.layoutIdx, { withRoom: hadRoom, endCount: hadRoom ? 4 : 2, primary: isF0 });
    // genLayout 覆写了 type 矩阵，按竖井注册表重新打标
    W.reapplyShafts();
  },

  // ════════════════════════════════════════════════════════════════════
  // 竖井操作
  // ════════════════════════════════════════════════════════════════════

  /**
   * 切换竖井（放置默认贯穿 当前活动层~顶层；已有则移除）
   */
  toggleShaft(cell: Cell): void {
    const S = App.Store;
    const W = App.World;
    const UI = App.UI;

    if (!W.inB(cell.x, cell.z)) return;

    if (W.hasShaftAt(cell.x, cell.z)) {
      if ((App.Command as any).do('移除竖井', () => W.removeShaft(cell.x, cell.z))) {
        UI.setProgress('已移除该处竖井');
      }
    } else {
      const from = S.activeFloorIdx;
      const to = S.floors.length - 1;
      if ((App.Command as any).do('放置竖井', () => W.placeShaft(cell.x, cell.z, from, to))) {
        const shaft = W.shaftAtCell(cell.x, cell.z);
        const fid = shaft ? shaft.id : '?';
        UI.setProgress(`已放置竖井${fid} (${cell.x},${cell.z}) · 贯穿 ${S.floors[from]?.name}~${S.floors[to]?.name}`);
      }
    }
  },

  /**
   * 修改竖井贯穿楼层范围（可撤销）
   */
  setShaftRange(shaft: Shaft, from: number, to: number): void {
    const S = App.Store;
    const W = App.World;

    if (!shaft) return;
    const f0 = Math.max(0, Math.min(from, to));
    const f1 = Math.min(S.floors.length - 1, Math.max(from, to));

    (App.Command as any).do('修改竖井贯穿楼层', () => {
      // 更新注册表范围，再重建格子缓存
      const s = W.shaftAtCell(shaft.x, shaft.z);
      if (s) {
        s.floorFrom = f0;
        s.floorTo = f1;
      }
      W.reapplyShafts();
    });
  },

  /**
   * 修改竖井所属管线系统（只影响寻路系统限制与着色，无需重打 RISER 格子）
   */
  setShaftSystem(shaft: Shaft, systemId: number): void {
    const W = App.World;
    if (!shaft) return;
    (App.Command as any).do('修改竖井系统', () => {
      const s = W.shaftAtCell(shaft.x, shaft.z);
      if (s) s.systemId = systemId;
    });
  },

  // ════════════════════════════════════════════════════════════════════
  // 楼层管理
  // ════════════════════════════════════════════════════════════════════

  /**
   * 切换楼层
   */
  switchFloor(idx: number): void {
    const S = App.Store;
    const RR = App.Render;
    const UI = App.UI;

    if (S.running) return;
    if (idx < 0 || idx >= S.floors.length || idx === S.activeFloorIdx) return;

    S.activeFloorIdx = idx;
    S.f = S.floors[idx];

    if (S.planView) RR.focusPlan();

    RR.rebuildAll();
    UI.renderFloorPanel();
    UI.renderFloorTabs();
    UI.updateDrawStatus();
    UI.updatePlanBadge();
    (App.Selection as any).afterChange();

    UI.setProgress('已切换到 ' + S.f.name + '（当前层可编辑）');
  },

  /**
   * 添加楼层
   */
  addFloor(): void {
    const S = App.Store;
    const RR = App.Render;
    const W = App.World;
    const UI = App.UI;
    const C = App.Config;

    if (S.running) return;
    if (S.floors.length >= C.FLOOR_NAMES.length) {
      UI.setProgress('已达楼层命名上限');
      return;
    }

    if (
      (App.Command as any).do('添加楼层', () => {
        S.floorCounter++;
        const n = S.floors.length;
        const f = (S as any).newFloor('F' + (n + 1), C.FLOOR_NAMES[n] || ('第' + (n + 1) + '层'), n * C.FLOOR_HEIGHT);

        S.floors.push(f);
        W.genLayout(f, S.layoutIdx, { withRoom: false, endCount: 2 });

        S.activeFloorIdx = S.floors.length - 1;
        S.f = f;
      })
    ) {
      RR.buildGrid();
      if (S.planView) RR.focusPlan();
      else RR.fitCamera();

      UI.setProgress('已添加 ' + S.f.name + ' · 切到该层可编辑');
    }
  },

  /**
   * 删除楼层
   */
  removeFloor(): void {
    const S = App.Store;
    const RR = App.Render;
    const W = App.World;
    const UI = App.UI;

    if (S.running) return;
    if (S.floors.length <= 1) {
      UI.setProgress('至少保留 1 个楼层');
      return;
    }

    const removed = S.f.name;

    if (
      (App.Command as any).do('删除楼层', () => {
        const i = S.activeFloorIdx;
        S.floors.splice(i, 1);
        (S as any).reindexFloors();

        // 调整竖井贯穿楼层索引（删除层 i 之后，>i 的索引整体 -1）
        S.shafts = S.shafts
          .map((s: Shaft) => {
            let from = s.floorFrom;
            let to = s.floorTo;
            if (i < from) { from--; to--; }         // 删除层在范围下方：整体上移
            else if (i >= from && i <= to) { to--; } // 删除层在范围内：终止层上移
            return { ...s, floorFrom: from, floorTo: to };
          })
          .filter((s: Shaft) => s.floorFrom <= s.floorTo && s.floorTo >= 0);

        // 按调整后的注册表重建 RISER 格子
        W.reapplyShafts();

        S.activeFloorIdx = Math.max(0, i - 1);
        S.f = S.floors[S.activeFloorIdx];
      })
    ) {
      RR.buildGrid();
      if (S.planView) RR.focusPlan();
      else RR.fitCamera();

      UI.setProgress('已删除 ' + removed + ' · 当前 ' + S.f.name);
    }
  },

  // ════════════════════════════════════════════════════════════════════
  // 绑定所有 DOM 事件
  // ════════════════════════════════════════════════════════════════════

  /**
   * 绑定所有事件
   */
  bindEvents(): void {
    const I = this;
    const S = App.Store;
    const RR = App.Render;
    const W = App.World;
    const UI = App.UI;
    const Cmd = App.Command;
    const cv = RR.canvas;

    // 单击事件
    cv.addEventListener('click', (ev: Event) => {
      if (S.running || (ev.target as HTMLElement) !== cv) return;
      if (S.mode === 'end' || S.mode === 'room' || S.mode === 'prop') return;

      const cell = I.cellFromEvent(ev);
      if (!cell) return;

      // 连线模式
      if (S.mode === 'line') {
        const last = S.curLine[S.curLine.length - 1];
        if (last && last.x === cell.x && last.z === cell.z) return;

        S.curLine.push(cell);
        UI.updateDrawStatus();
        UI.setProgress(`连线中：第 ${S.curLine.length} 个顶点 · 双击/回车 完成 · Esc 取消`);
        RR.buildPreview();
        return;
      }

      // 竖井模式
      if (S.mode === 'shaft') {
        I.toggleShaft(cell);
        return;
      }

      // 编辑模式：切换走管/禁行
      const T = App.Config.T;
      const t = S.f.type[cell.x][cell.z];
      if (t === T.ROOM || t === T.END) return;

      if (
        (Cmd as any).do('切换走管/禁行', () => {
          S.f.type[cell.x][cell.z] = t === T.FORBID ? T.WALK : T.FORBID;
        })
      ) {
        UI.setProgress('已修改通道，点击"生成连接路径"重算');
      }
    });

    // 双击完成线条
    cv.addEventListener('dblclick', (ev: Event) => {
      if (S.mode !== 'line' || S.running || (ev.target as HTMLElement) !== cv) return;
      I.finishLine();
    });

    // 鼠标移动（连线预览）
    cv.addEventListener('mousemove', (ev: Event) => {
      if (S.mode !== 'line') {
        if (RR.hoverMesh.visible) RR.hoverMesh.visible = false;
        return;
      }

      const cell = I.cellFromEvent(ev);
      if (!cell) {
        if (S.hoverCell) {
          S.hoverCell = null;
          RR.buildPreview();
        }
        return;
      }

      if (!S.hoverCell || S.hoverCell.x !== cell.x || S.hoverCell.z !== cell.z) {
        S.hoverCell = cell;
        RR.buildPreview();
      }
    });

    // 键盘快捷键
    addEventListener('keydown', (ev: KeyboardEvent) => {
      const tag = (ev.target && (ev.target as HTMLElement).tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;

      if ((ev.ctrlKey || ev.metaKey) && !ev.shiftKey && (ev.key === 'z' || ev.key === 'Z')) {
        ev.preventDefault();
        (Cmd as any).undo();
      } else if ((ev.ctrlKey || ev.metaKey) && ev.shiftKey && (ev.key === 'z' || ev.key === 'Z' || ev.key === 'y' || ev.key === 'Y')) {
        ev.preventDefault();
        (Cmd as any).redo();
      } else if (S.mode === 'line' && ev.key === 'Enter') {
        ev.preventDefault();
        I.finishLine();
      } else if (S.mode === 'line' && ev.key === 'Escape') {
        I.cancelLine();
      }
    });

    // 指针事件（拖拽）
    cv.addEventListener('pointerdown', (ev: PointerEvent) => {
      if (S.running || ev.button !== 0) return;

      // 属性模式：pointerdown 即时选中
      if (S.mode === 'prop') {
        (App.Selection as any).selectFromEvent(ev);
        return;
      }

      // 末端模式
      if (S.mode === 'end') {
        if ((App.Layers as any).isKindLocked('end')) return;

        const endMesh = I.raycastEnd(ev);
        if (endMesh) {
          S.dragEnd = endMesh.userData.end;
          S.downOnEnd = S.dragEnd;
          S.dragMoved = false;
          (RR.activeControls() as any).enabled = false;
          (Cmd as any).beginDrag();
        } else {
          S.downCell = I.cellFromEvent(ev);
          S.downX = ev.clientX;
          S.downY = ev.clientY;
        }
        return;
      }

      // 机房模式
      if (S.mode === 'room') {
        if ((App.Layers as any).isKindLocked('room')) return;

        const cell = I.cellFromEvent(ev);
        const r = cell ? (W as any).roomAtCell(cell) : null;
        if (r) {
          S.dragRoom = r;
          S.dragMoved = false;
          (RR.activeControls() as any).enabled = false;
          if (cell) {
            S.roomGrabDX = cell.x - r.x;
            S.roomGrabDZ = cell.z - r.z;
          }
          (Cmd as any).beginDrag();
        } else {
          S.downCell = cell;
          S.downX = ev.clientX;
          S.downY = ev.clientY;
        }
        return;
      }
    }, true);

    addEventListener('pointermove', (ev: PointerEvent) => {
      if (S.mode === 'end' && S.dragEnd) {
        const cell = I.cellFromEvent(ev);
        if (cell && (cell.x !== S.dragEnd.x || cell.z !== S.dragEnd.z)) {
          S.dragMoved = true;
          I._liveMoveEnd(S.dragEnd, cell);
        }
      } else if (S.mode === 'room' && S.dragRoom) {
        const cell = I.cellFromEvent(ev);
        if (!cell) return;
        const a = W.clampAnchor(S.dragRoom, cell.x - S.roomGrabDX, cell.z - S.roomGrabDZ);
        if (a.x !== S.dragRoom.x || a.z !== S.dragRoom.z) {
          S.dragMoved = true;
          I._liveMoveRoom(S.dragRoom, cell);
        }
      }
    });

    addEventListener('pointerup', (ev: PointerEvent) => {
      if (S.mode === 'end') {
        if (S.dragEnd) {
          (RR.activeControls() as any).enabled = true;
          const e = S.dragEnd;
          S.dragEnd = null;

          if (S.dragMoved) {
            (Cmd as any).endDrag('拖动末端');
          } else {
            (Cmd as any).cancelDrag();
            I.deleteEnd(e);
          }

          S.dragMoved = false;
          S.downOnEnd = null;
          S.downCell = null;
          return;
        }

        if (S.downCell) {
          if (Math.hypot(ev.clientX - S.downX, ev.clientY - S.downY) < 6) {
            I.addEnd(S.downCell);
          }
          S.downCell = null;
        }
      } else if (S.mode === 'room') {
        if (S.dragRoom) {
          (RR.activeControls() as any).enabled = true;
          const r = S.dragRoom;
          S.dragRoom = null;

          if (S.dragMoved) {
            (Cmd as any).endDrag('拖动机房');
          } else {
            (Cmd as any).cancelDrag();
            I.deleteRoom(r);
          }

          S.dragMoved = false;
          S.downCell = null;
          return;
        }

        if (S.downCell) {
          if (Math.hypot(ev.clientX - S.downX, ev.clientY - S.downY) < 6) {
            I.addRoom(S.downCell);
          }
          S.downCell = null;
        }
      }
    });
  },
};

export default App.Interaction;
export const Interaction = App.Interaction;
